// Serialize an IRealChart back to an `irealb://` URL (and an iReal-style HTML
// export). The block scramble is self-inverse, so we reuse it to re-obfuscate;
// chords/barlines are emitted as plain tokens (no Kcl/LZ/XyQ abbreviations),
// which iReal Pro reads back without trouble.

import { scramble, MUSIC_PREFIX } from './unscramble';
import type { IRealChart, IRealChordRef, IRealMeasure } from './types';

export interface IRealExportWarning {
  code: 'feel-degraded-to-staff-text';
  measureIndex: number;
  measureId: string;
  label: string;
  message: string;
}

/** An iReal payload plus every authored feature that had to be degraded. */
export interface IRealExportResult {
  url: string;
  warnings: IRealExportWarning[];
}

function refToken(ref: IRealChordRef): string {
  // iReal spells the 6/9 chord `69`: a `/` in the token reads as a slash
  // bass, so "C6/9" came back a plain C6. Charts saved before the catalogue
  // spelled it `69` (and MusicXML chord text) can still carry "6/9".
  const quality = ref.quality.replace('6/9', '69');
  return ref.root + quality + (ref.bass ? `/${ref.bass}` : '');
}

function chordToken(chord: IRealMeasure['chords'][number]): string {
  if (chord.noChord) return 'n';
  let t = refToken(chord);
  if (chord.alternate) t += `(${refToken(chord.alternate)})`;
  return t;
}

function openToken(open: IRealMeasure['open']): string {
  if (open === 'repeat') return '{';
  if (open === 'double') return '[';
  return '';
}

function closeToken(close: IRealMeasure['close']): string {
  switch (close) {
    case 'final':
      return 'Z';
    case 'repeat':
      return '}';
    case 'double':
      return ']';
    default:
      return '|';
  }
}

/**
 * iReal's section mark is `*` and ONE letter: everything after the letter is
 * read as music, so "Intro" came back as a section `I`, an N.C. chord and a
 * two-bar repeat, and "Verse" latched every later chord small. Names from
 * the measure editor, openbook's `\myMark` and MusicXML rehearsal marks are
 * free text, so a longer name folds to iReal's own intro and verse marks or
 * its first letter; a name with no letter to keep writes no mark.
 */
function sectionToken(name: string): string {
  const trimmed = name.trim();
  if (/^[A-Za-z]$/.test(trimmed)) return `*${trimmed}`;
  if (/^(intro|in\b)/i.test(trimmed)) return '*i';
  if (/^(verse|v\b)/i.test(trimmed)) return '*v';
  return /^[A-Za-z]/.test(trimmed) ? `*${trimmed[0].toUpperCase()}` : '';
}

function keyToIReal(key: string | undefined): string {
  if (!key) return 'C';
  const m = /^([A-G][#b]?)\s*(minor|min|m)?$/i.exec(key.trim());
  if (!m) return key.trim();
  return m[2] ? `${m[1]}-` : m[1];
}

/** iReal stores composers as "Last First"; undo the parser's swap on export
 * so the name doesn't flip on every round trip. */
function composerToIReal(composer: string | undefined): string {
  const parts = (composer ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 2) return `${parts[1]} ${parts[0]}`;
  return (composer ?? '').trim();
}

/** Build the plain (unscrambled) iReal music token string for a chart. */
export function buildMusicTokens(chart: IRealChart): string {
  let out = '';
  let small = false; // chord-size state, toggled by `s`/`l` across the chart
  chart.measures.forEach((m, idx) => {
    out += openToken(m.open);
    if (m.spacer) out += 'Y'.repeat(m.spacer);
    if (m.section) out += sectionToken(m.section);
    // 12/8 is the single two-digit token, literally "T12" (not T-12-8).
    if (m.timeSig)
      out += m.timeSig[0] === 12 && m.timeSig[1] === 8 ? 'T12' : `T${m.timeSig[0]}${m.timeSig[1]}`;
    if (m.ending != null) out += `N${m.ending}`;
    if (m.segno) out += 'S';
    if (m.coda) out += 'Q';
    if (m.fermata) out += 'f';
    // *74 is the vertical offset the parser reads back as "above the staff".
    if (m.staffText) out += m.staffTextAbove ? `<*74${m.staffText}>` : `<${m.staffText}>`;
    if (m.directive) out += `<${m.directive}>`;
    if (m.barRepeat === 1) out += 'x';
    else if (m.barRepeat === 2) out += 'r';
    else {
      out += m.chords
        .map((c) => {
          let pre = '';
          if (!!c.small !== small) {
            pre = c.small ? 's' : 'l';
            small = !!c.small;
          }
          // Trailing empty cells make the chord span `beats` layout cells, so
          // harmonic rhythm (e.g. 3+1 in a bar) survives the round trip — the
          // parser derives beats from cell spans, and bare commas erased them.
          const pad = ' '.repeat(Math.max(0, Math.min(15, (c.beats || 1) - 1)));
          return pre + chordToken(c) + pad;
        })
        .join(',');
    }
    // The end marker rides at the bar's end: iReal draws it as a sign and the
    // parser reads it per bar, so its cell position inside the bar is moot.
    if (m.endMarker) out += 'U';
    const isLast = idx === chart.measures.length - 1;
    out += closeToken(m.close ?? (isLast ? 'final' : 'single'));
  });
  return out;
}

function encodeIRealURL(chart: IRealChart): string {
  const tokens = buildMusicTokens(chart);
  const music = MUSIC_PREFIX + encodeURIComponent(scramble(tokens));
  // Empty text fields are emitted as a single space (trimmed away on parse):
  // two adjacent empties would join into "===", the PLAYLIST separator, and
  // the reparse would cut the song in half at that point.
  const pad = (s: string) => (s.trim() ? s : ' ');
  const fields = [
    encodeURIComponent(chart.title || 'Untitled'),
    encodeURIComponent(pad(composerToIReal(chart.composer))),
    '',
    encodeURIComponent(pad(chart.style ?? '')),
    encodeURIComponent(keyToIReal(chart.key)),
    '',
    music,
    encodeURIComponent(chart.style ?? ''),
    String(chart.tempo ?? 0),
    String(chart.repeats ?? 0),
  ];
  return `irealb://${fields.join('=')}===`;
}

/**
 * Serialize a chart and report lossy-but-visible conversions.
 *
 * iReal has no typed Feel field. Keep each cue visible as staff text rather
 * than dropping it, while reporting that its typed playback meaning (and any
 * within-measure offset) will not survive outside this app.
 */
export function exportIRealURL(chart: IRealChart): IRealExportResult {
  const warnings: IRealExportWarning[] = [];
  const projected: IRealChart = {
    ...chart,
    measures: chart.measures.map((measure, measureIndex) => {
      const label = measure.feel?.label.trim();
      if (!label) return measure;

      const existing = measure.staffText?.trim();
      const sameText = existing?.localeCompare(label, undefined, { sensitivity: 'accent' }) === 0;
      const staffText = !existing || sameText ? label : `${label} · ${existing}`;
      const moved = !!measure.feel?.offset && measure.feel.offset.num !== 0;
      warnings.push({
        code: 'feel-degraded-to-staff-text',
        measureIndex,
        measureId: measure.id,
        label,
        message: `Measure ${measureIndex + 1}: Feel “${label}” was exported as visible staff text${moved ? ' at the start of the measure' : ''}; its typed playback meaning is not representable in iReal Pro.`,
      });
      return {
        ...measure,
        staffText,
        // A cue without existing prose uses the conventional above-staff
        // placement. Combined prose keeps the user's authored side.
        staffTextAbove: existing ? measure.staffTextAbove : true,
      };
    }),
  };
  return { url: encodeIRealURL(projected), warnings };
}

/** Compatibility string helper. New export surfaces should use the report. */
export function toIRealURL(chart: IRealChart): string {
  return exportIRealURL(chart).url;
}

/** An iReal-Pro-style single-song HTML export (a link plus a readable title). */
export function toIRealHTML(chart: IRealChart): string {
  const url = exportIRealURL(chart).url;
  const title = chart.title || 'Untitled';
  const composer = chart.composer ? ` - ${chart.composer}` : '';
  const safe = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><title>${safe(title)}</title></head>
<body>
<h1>iReal Pro</h1>
<p>Song: 1</p>
<a href="${safe(url)}">${safe(title)}${safe(composer)}</a>
</body>
</html>
`;
}
